// TermsOfService.js
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  FaArrowLeft, 
  FaGavel, 
  FaFileContract, 
  FaUserCheck, 
  FaLock, 
  FaCopyright, 
  FaExclamationTriangle,
  FaShieldAlt,
  FaClipboardList,
  FaMoneyBillWave,
  FaHandshake,
  FaServer,
  FaGlobe,
  FaEnvelope,
  FaLink,
  FaShoppingCart,
  FaUndo
} from "react-icons/fa";
import "./TermsOfService.css";

function TermsOfService() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleBack = () => {
    navigate(-1);
  };

  // const effectiveDate = "July 15, 2026";

  return (
    <div className="terms-container">
      {/* Header Section */}
      <div className="terms-header">
        <div className="terms-header-content">
          <button className="back-button" onClick={handleBack}>
            <FaArrowLeft /> Back
          </button>
          <div className="terms-header-icon">
            <FaGavel />
          </div>
          <h1>Terms and Conditions</h1>
          <p className="terms-subtitle">
            Please read these terms carefully before using our services. By using our platform, 
            you agree to be bound by these terms.
          </p>
          {/* <div className="effective-date">
            <FaFileContract />
            <span>Effective Date: {effectiveDate}</span>
          </div> */}
        </div>
      </div>

      {/* Main Content */}
      <div className="terms-content">
        {/* 1. Acceptance of Terms */}
        <div className="terms-section">
          <h2>
            <FaHandshake className="section-icon" />
            1. Acceptance of Terms
          </h2>
          <p>
            By accessing or using the Alvanium Technologies website or services, you agree to be 
            bound by these Terms and Conditions.
          </p>
          <p className="terms-notice">
            If you do not agree with these terms, please discontinue use of our website and services.
          </p>
        </div>

        {/* 2. About Us */}
        <div className="terms-section">
          <h2>
            <FaServer className="section-icon" />
            2. About Us
          </h2>
          <p>
            Alvanium Technologies develops software products, SaaS applications, marketplace apps, 
            and related technology solutions.
          </p>
        </div>

        {/* 3. Use of Our Website */}
        <div className="terms-section">
          <h2>
            <FaGlobe className="section-icon" />
            3. Use of Our Website
          </h2>
          <p>You agree to use our website only for lawful purposes. You must not:</p>
          <ul>
            <li>Violate applicable laws</li>
            <li>Attempt unauthorized access</li>
            <li>Distribute malware</li>
            <li>Interfere with website functionality</li>
            <li>Use the website to transmit harmful or unlawful content</li>
            <li>Copy or misuse website content without permission</li>
          </ul>
        </div>

        {/* 4. Intellectual Property */}
        <div className="terms-section">
          <h2>
            <FaCopyright className="section-icon" />
            4. Intellectual Property
          </h2>
          <p>All website content, including:</p>
          <ul>
            <li>Logos</li>
            <li>Software</li>
            <li>Source code</li>
            <li>Graphics</li>
            <li>Documentation</li>
            <li>Text</li>
            <li>Product names</li>
            <li>Designs</li>
          </ul>
          <p>
            is owned by or licensed to Alvanium Technologies and is protected by applicable 
            intellectual property laws.
          </p>
          <p>
            You may not reproduce, modify, distribute, or create derivative works without our 
            written permission.
          </p>
        </div>

        {/* 5. Products and Services */}
        <div className="terms-section">
          <h2>
            <FaShoppingCart className="section-icon" />
            5. Products and Services
          </h2>
          <p>We may offer software products, subscriptions, and related services.</p>
          <p>Features, pricing, and availability may change without prior notice.</p>
        </div>

        {/* 6. Payments */}
        <div className="terms-section">
          <h2>
            <FaMoneyBillWave className="section-icon" />
            6. Payments
          </h2>
          <ul>
            <li>Some services require payment.</li>
            <li>Payments are securely processed by <strong>Lemon Squeezy</strong>.</li>
            <li>
              By purchasing our services, you agree to the applicable pricing and billing terms.
            </li>
          </ul>
        </div>

        {/* 7. Refunds */}
        <div className="terms-section">
          <h2>
            <FaUndo className="section-icon" />
            7. Refunds
          </h2>
          <p>
            Refunds are subject to our Refund Policy or the applicable terms of the platform 
            through which the purchase was made.
          </p>
        </div>

        {/* 8. User Responsibilities */}
        <div className="terms-section">
          <h2>
            <FaUserCheck className="section-icon" />
            8. User Responsibilities
          </h2>
          <p>You agree to:</p>
          <ul>
            <li>Provide accurate information</li>
            <li>Maintain the security of your account</li>
            <li>Comply with applicable laws</li>
            <li>Respect intellectual property rights</li>
            <li>Use our services responsibly</li>
          </ul>
        </div>

        {/* 9. Third-Party Links */}
        <div className="terms-section">
          <h2>
            <FaLink className="section-icon" />
            9. Third-Party Links
          </h2>
          <p>Our website may contain links to third-party websites.</p>
          <p>
            We are not responsible for the content, policies, or practices of those websites.
          </p>
        </div>

        {/* 10. Disclaimer */}
        <div className="terms-section">
          <h2>
            <FaExclamationTriangle className="section-icon" />
            10. Disclaimer
          </h2>
          <p>
            Our website and services are provided on an <strong>"as is"</strong> and 
            <strong>"as available"</strong> basis.
          </p>
          <p>
            We make no warranties regarding uninterrupted availability, accuracy, or suitability 
            for any particular purpose.
          </p>
        </div>

        {/* 11. Limitation of Liability */}
        <div className="terms-section">
          <h2>
            <FaShieldAlt className="section-icon" />
            11. Limitation of Liability
          </h2>
          <p>
            To the maximum extent permitted by law, Alvanium Technologies shall not be liable for 
            any indirect, incidental, special, consequential, or punitive damages arising from your 
            use of our website or services.
          </p>
        </div>

        {/* 12. Indemnification */}
        <div className="terms-section">
          <h2>
            <FaClipboardList className="section-icon" />
            12. Indemnification
          </h2>
          <p>
            You agree to indemnify and hold harmless Alvanium Technologies, its employees, affiliates, 
            and partners from any claims, damages, or liabilities arising from your misuse of the 
            website or violation of these Terms.
          </p>
        </div>

        {/* 13. Termination */}
        <div className="terms-section">
          <h2>
            <FaLock className="section-icon" />
            13. Termination
          </h2>
          <p>
            We reserve the right to suspend or terminate access to our website or services at our 
            sole discretion if these Terms are violated or where necessary to protect our business, 
            users, or legal interests.
          </p>
        </div>

        {/* 14. Governing Law */}
        <div className="terms-section">
          <h2>
            <FaGavel className="section-icon" />
            14. Governing Law
          </h2>
          <p>
            These Terms and Conditions shall be governed by and interpreted in accordance with the 
            laws of India.
          </p>
          <p>
            Any disputes shall be subject to the exclusive jurisdiction of the competent courts in India.
          </p>
        </div>

        {/* 15. Changes to These Terms */}
        <div className="terms-section">
          <h2>
            <FaFileContract className="section-icon" />
            15. Changes to These Terms
          </h2>
          <p>We may revise these Terms and Conditions from time to time.</p>
          <p>Updated versions become effective upon publication on this website.</p>
          <p>
            Continued use of the website constitutes acceptance of the revised Terms.
          </p>
        </div>

        {/* 16. Contact Information */}
        <div className="terms-section">
          <h2>
            <FaEnvelope className="section-icon" />
            16. Contact Information
          </h2>
          <p>
            For any questions regarding these Terms and Conditions, please contact:
          </p>
          <div className="contact-info-box">
            <p>
              <strong>Alvanium Technologies</strong><br />
              Website: <a href="https://alvanium.com" target="_blank" rel="noopener noreferrer">https://alvanium.com</a><br />
              Email: <a href="mailto:info@alvanium.com">info@alvanium.com</a>
            </p>
          </div>
          <p className="company-tagline">
            <em>Alvanium – Intelligence Behind Every Workflow</em>
          </p>
        </div>
      </div>
    </div>
  );
}

export default TermsOfService;